import {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type JSX,
  type Ref,
} from 'react';

import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.js';
import type { AutomationSchedule, ScheduleRule } from '@/integration/types.js';
import {
  parseSchedule,
  type AutomationPlanInput,
  type ParsedCronSchedule,
  type ParsedPlannedSchedule,
  type ParsedSchedule,
} from '@/lib/api/automations.js';

import { cronHumanize } from './cron-humanize.js';
import { MODEL_DATES_NOTE } from './next-run.js';
import { browserTz } from './tz.js';

type PresetFrequency = 'daily' | 'weekly' | 'hourly';
type Frequency = PresetFrequency | 'custom';

interface PickerState {
  frequency: Frequency;
  /** 0–23 */
  hour: number;
  /** 0–59 */
  minute: number;
  /** 0 (Sunday) – 6 (Saturday); only used for weekly. */
  weekday: number;
  /** The cron behind Custom mode: the stored one, or the last interpretation's. Null until one exists. */
  cron: string | null;
  /** True when a planned schedule (no cron; the words decide each run) stands behind Custom mode. */
  planned: boolean;
  /** The rule that computes the planned schedule's runs; null when a model picks its dates. */
  rule: ScheduleRule | null;
  /** The words the custom cron was made from; empty when the stored cron came without any. */
  scheduleText: string;
  /** The timezone used to interpret the stored custom cron. */
  cronTimezone: string | null;
}

/** An answer the words can be saved with. */
type AcceptedSchedule = ParsedCronSchedule | ParsedPlannedSchedule;

/** What the schedule interpreter said about the words in the box. */
type Interpretation =
  | { status: 'idle' }
  | { status: 'busy' }
  | { status: 'ok'; result: AcceptedSchedule }
  | {
      status: 'unsupported';
      message: string;
      /** True when the interpreter could not be reached, rather than refusing the words. */
      failed: boolean;
    };

/** The interpreter's answer, or `failed` when it threw or never answered. */
type InterpretOutcome = ParsedSchedule | { failed: true };

/** What a save gets once the picker has settled the schedule. */
export type ScheduleForSave =
  /** Nothing to settle: the schedule the picker last emitted stands. */
  | { status: 'current' }
  /** The schedule, its words and the first run (planned only) to save. */
  | {
      status: 'ready';
      schedule: AutomationSchedule;
      scheduleText: string;
      plan: AutomationPlanInput | null;
    }
  /** Nothing can be saved; the picker shows why under the words and has moved focus there. */
  | { status: 'refused' }
  /** The words, the zone or the frequency changed while the save waited: save nothing, keep the edit. */
  | { status: 'superseded' };

/** A settled schedule a save can go ahead with. */
export type SavableSchedule = Extract<
  ScheduleForSave,
  { status: 'current' | 'ready' }
>;

/** Lets an editor settle the schedule before it saves. */
export interface SchedulePickerHandle {
  /** Waits for, or starts, the interpretation the words need, then says what to save. */
  settleForSave: () => Promise<ScheduleForSave>;
}

const WEEKDAY_OPTIONS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' },
];

/** Shown when the interpreter could not be reached at all (a network failure, not an answer). */
const INTERPRET_FAILED = "Couldn't interpret the schedule. Try again.";

/** Shown when a save finds Custom mode with no words in it. */
const WORDS_REQUIRED = 'Describe the schedule in your words.';

/** How long the words rest before they are interpreted on their own. */
const AUTO_INTERPRET_DELAY_MS = 800;

/**
 * How long a save waits for the interpreter before it saves the words as planned. Longer than the
 * platform's own 40 s budget, so a slow answer that does arrive is kept rather than replaced.
 */
const SAVE_INTERPRET_TIMEOUT_MS = 45_000;

function clampInt(
  value: number,
  min: number,
  max: number,
  fallback: number,
): number {
  if (!Number.isInteger(value) || value < min || value > max) return fallback;
  return value;
}

/** A plain integer cron field within range, or null for a star, a list, a range, a step or an out-of-range value. */
function cronField(
  raw: string | undefined,
  min: number,
  max: number,
): number | null {
  if (raw === undefined || !/^\d+$/.test(raw)) return null;
  const value = Number(raw);
  return value >= min && value <= max ? value : null;
}

/** Build a 5-field cron expression from a preset. */
function presetCron(state: PickerState, frequency: PresetFrequency): string {
  const { hour, minute, weekday } = state;
  switch (frequency) {
    case 'hourly':
      return `${minute} * * * *`;
    case 'weekly':
      return `${minute} ${hour} * * ${weekday}`;
    case 'daily':
      return `${minute} ${hour} * * *`;
  }
}

/**
 * Read a cron back into one of the three presets, or null when it is any other shape (a weekday
 * range, a day of month, a step) — those open in Custom mode so saving never rewrites them.
 */
function presetFromCron(
  cron: string,
): Pick<PickerState, 'frequency' | 'hour' | 'minute' | 'weekday'> | null {
  const fields = cron.trim().split(/\s+/);
  if (fields.length !== 5) return null;
  const [minuteRaw, hourRaw, dayOfMonthRaw, monthRaw, dayOfWeekRaw] = fields;
  if (dayOfMonthRaw !== '*' || monthRaw !== '*') return null;
  const minute = cronField(minuteRaw, 0, 59);
  if (minute === null) return null;
  if (hourRaw === '*' && dayOfWeekRaw === '*') {
    return { frequency: 'hourly', hour: 9, minute, weekday: 1 };
  }
  const hour = cronField(hourRaw, 0, 23);
  if (hour === null) return null;
  if (dayOfWeekRaw === '*')
    return { frequency: 'daily', hour, minute, weekday: 1 };
  const weekday = cronField(dayOfWeekRaw, 0, 7);
  if (weekday === null) return null;
  return { frequency: 'weekly', hour, minute, weekday: weekday % 7 };
}

/**
 * Parse an existing schedule into picker state. No schedule at all (CreateAutomationDialog before
 * the user has touched the picker) defaults to daily 09:00; a cron no preset can express opens in
 * Custom mode with the words it was made from, when they are known.
 */
function scheduleToState(
  schedule: AutomationSchedule | undefined,
  scheduleText: string | null,
): PickerState {
  const fallback: PickerState = {
    frequency: 'daily',
    hour: 9,
    minute: 0,
    weekday: 1,
    cron: null,
    planned: false,
    rule: null,
    scheduleText: '',
    cronTimezone: null,
  };
  if (schedule?.type === 'planned') {
    return {
      ...fallback,
      frequency: 'custom',
      planned: true,
      rule: schedule.rule ?? null,
      scheduleText: scheduleText ?? '',
      cronTimezone: schedule.tz || browserTz(),
    };
  }
  if (!schedule?.cron) return fallback;
  const preset = presetFromCron(schedule.cron);
  if (preset && !scheduleText) return { ...fallback, ...preset };
  return {
    ...fallback,
    ...preset,
    frequency: 'custom',
    cron: schedule.cron,
    scheduleText: scheduleText ?? '',
    cronTimezone: schedule.tz || browserTz(),
  };
}

/** The interpreter's answer, with a failure to reach it rendered as an outcome too. */
async function interpretWords(
  text: string,
  tz: string,
): Promise<InterpretOutcome> {
  try {
    return await parseSchedule(text, tz);
  } catch {
    return { failed: true };
  }
}

/** The outcome, or `failed` once the save has waited long enough. */
function withSaveTimeout(
  outcome: Promise<InterpretOutcome>,
): Promise<InterpretOutcome> {
  return new Promise((resolve) => {
    const timer = setTimeout(
      () => resolve({ failed: true }),
      SAVE_INTERPRET_TIMEOUT_MS,
    );
    void outcome.then((answer) => {
      clearTimeout(timer);
      resolve(answer);
    });
  });
}

/**
 * The first run Interpret previewed for a planned answer, when the planner said why. A rule
 * computes its own runs, so a planned answer with one sends no plan.
 */
function firstPlan(result: AcceptedSchedule): AutomationPlanInput | null {
  if (result.kind !== 'planned' || result.rule) return null;
  const firstRun = result.nextFires[0];
  if (firstRun === undefined || result.reason === null) return null;
  return { at: firstRun, reason: result.reason };
}

/** The schedule behind Custom mode, or null when no interpretation stands behind it yet. */
function storedCustomSchedule(
  state: PickerState,
  tz: string,
): AutomationSchedule | null {
  if (state.planned) return plannedSchedule(tz, state.rule);
  return state.cron === null ? null : { cron: state.cron, tz };
}

/** A planned schedule in a zone, carrying its rule when one computes the runs. */
function plannedSchedule(
  tz: string,
  rule: ScheduleRule | null | undefined,
): AutomationSchedule {
  return rule ? { type: 'planned', tz, rule } : { type: 'planned', tz };
}

/** "Weekdays at 08:30" → "weekdays at 08:30", so the summary reads on after "Runs". */
function asClause(summary: string): string {
  return summary.charAt(0).toLowerCase() + summary.slice(1);
}

/** A run instant as the schedule's own zone shows it: "Thu 8 Oct, 08:30". */
function formatRun(iso: string, tz: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(iso));
}

export interface SchedulePickerProps {
  /** Current schedule (drives initial picker state). */
  value: AutomationSchedule | undefined;
  /** Called whenever the user changes the schedule. */
  onChange: (schedule: AutomationSchedule) => void;
  /** Optional id prefix for the labelled controls. */
  idPrefix?: string;
  /**
   * Lock the Frequency control (visible but disabled, pinned to Daily) — used
   * by the Reminder type: a run-limit-1 automation fires once at the next
   * occurrence of the chosen time, so cadence is not the user's choice there.
   * The emitted cron is the daily shape; the run limit is what makes it one-shot.
   */
  lockFrequency?: boolean;
  /** Enable Custom editing for callers that handle schedule text and validity. */
  allowCustom?: boolean;
  /** The words a custom schedule was made from, shown when it opens in Custom mode. */
  scheduleText?: string | null;
  /** The words behind the schedule just emitted: the interpreted text, or null once a preset replaces it. */
  onScheduleTextChange?: (text: string | null) => void;
  /**
   * Whether what the picker shows is a schedule that can be saved. False while Custom mode holds
   * words that have not been interpreted yet, or that the interpreter refused.
   */
  onValidityChange?: (valid: boolean) => void;
  /** Lets the editor settle the words in Custom mode before it saves. */
  ref?: Ref<SchedulePickerHandle>;
}

/**
 * Schedule picker: Daily / Weekly / Hourly presets plus Custom, where the user writes the schedule
 * in their own words and the platform turns them into a cron, or into a planned schedule when no
 * cron can hold them. Emits a `{cron, tz}` or `{type: 'planned', tz}` schedule. Shared by
 * CreateAutomationDialog and AutomationDetail. `tz` is taken from the
 * existing schedule when editing, otherwise the browser timezone. The words are interpreted on
 * their own after a pause in typing and on blur; `ref.settleForSave` finishes the job on save.
 */
export function SchedulePicker({
  value,
  onChange,
  idPrefix = 'schedule',
  lockFrequency = false,
  allowCustom = false,
  scheduleText = null,
  onScheduleTextChange,
  onValidityChange,
  ref,
}: SchedulePickerProps): JSX.Element {
  /**
   * A defined schedule with a missing (not merely empty or invalid) cron is an on-demand (manual)
   * automation. `value === undefined` is the "not yet set" case (CreateAutomationDialog before the
   * user has touched anything) and still gets the normal editable Daily 09:00 picker below. An empty
   * string is invalid-but-present data, not "no schedule", and must not read as on-demand.
   */
  const isOnDemand =
    value !== undefined &&
    value.type !== 'planned' &&
    (value.cron === undefined || value.cron === null);

  const [state, setState] = useState<PickerState>(() =>
    scheduleToState(value, scheduleText),
  );
  const [customText, setCustomText] = useState(state.scheduleText);
  const [interpretation, setInterpretation] = useState<Interpretation>({
    status: 'idle',
  });
  const tz = value?.tz || browserTz();
  const interpretationVersion = useRef(0);
  /** The interpretation in flight, so a save can wait for it instead of asking again. */
  const inFlight = useRef<Promise<InterpretOutcome> | null>(null);
  const customInputRef = useRef<HTMLInputElement | null>(null);
  /** The zone of a schedule the picker just emitted in place of its own, until the editor passes it back. */
  const adoptedTimezone = useRef<string | null>(null);
  /**
   * When locked (the Reminder type), the frequency is pinned to Daily regardless of any stale
   * internal state carried over from a prior selection — both what is rendered and what is emitted.
   */
  const effectiveFrequency: Frequency = lockFrequency
    ? 'daily'
    : state.frequency;
  /** Custom words that no schedule stands behind yet: typed but not interpreted, or refused. */
  const customPending =
    (state.cron === null && !state.planned) ||
    customText.trim() !== state.scheduleText.trim() ||
    state.cronTimezone !== tz;
  const valid =
    effectiveFrequency !== 'custom' ||
    (!customPending && interpretation.status !== 'busy');
  /** Words that need an interpretation nobody has asked for yet. */
  const awaitingInterpretation =
    allowCustom &&
    effectiveFrequency === 'custom' &&
    customPending &&
    customText.trim().length > 0 &&
    interpretation.status === 'idle';

  /**
   * A new zone makes the last answer stale, unless the picker caused it by adopting an answer given
   * in that zone: then the answer, and the plan it previewed, still stand.
   */
  useEffect(() => {
    const selfCaused = tz === adoptedTimezone.current;
    adoptedTimezone.current = null;
    if (selfCaused) return;
    interpretationVersion.current += 1;
    setInterpretation({ status: 'idle' });
  }, [tz]);

  useEffect(() => {
    interpretationVersion.current += 1;
    setInterpretation({ status: 'idle' });
  }, [lockFrequency]);

  useEffect(
    () => () => {
      interpretationVersion.current += 1;
    },
    [],
  );

  useEffect(() => {
    onValidityChange?.(valid);
  }, [valid, onValidityChange]);

  /** The words rest, then interpret themselves; any new keystroke or zone starts the wait over. */
  useEffect(() => {
    if (!awaitingInterpretation) return;
    const timer = setTimeout(() => void interpret(), AUTO_INTERPRET_DELAY_MS);
    return () => clearTimeout(timer);
  }, [awaitingInterpretation, customText, tz]);

  useImperativeHandle(ref, () => ({ settleForSave }));

  /**
   * Preserve `type` if the incoming schedule carried one, normalized to 'cron' — this is the
   * cron-editing path, so any prior type (e.g. a drifted 'manual') is corrected rather than
   * silently dropped. Schedules that never had a `type` field (legacy rows) stay without one.
   */
  function cronSchedule(cron: string, zone: string): AutomationSchedule {
    return value?.type !== undefined
      ? { type: 'cron', cron, tz: zone }
      : { cron, tz: zone };
  }

  function emitCron(cron: string, zone: string): void {
    onChange(cronSchedule(cron, zone));
  }

  /** The schedule an accepted answer stands for: its cron, or a planned schedule in its zone with any rule. */
  function acceptedSchedule(result: AcceptedSchedule): AutomationSchedule {
    if (result.kind === 'planned')
      return plannedSchedule(result.tz, result.rule);
    return cronSchedule(result.cron, result.tz);
  }

  /** The cadence a preset emits: Daily when locked. A Custom state never reaches here. */
  function presetFrequency(next: PickerState): PresetFrequency {
    if (lockFrequency || next.frequency === 'custom') return 'daily';
    return next.frequency;
  }

  function emitPreset(next: PickerState): void {
    interpretationVersion.current += 1;
    setState({
      ...next,
      cron: null,
      planned: false,
      rule: null,
      scheduleText: '',
      cronTimezone: null,
    });
    setCustomText('');
    setInterpretation({ status: 'idle' });
    emitCron(presetCron(next, presetFrequency(next)), tz);
    onScheduleTextChange?.(null);
  }

  /** Custom re-emits the schedule it already holds, if any; otherwise nothing until the words are interpreted. */
  function selectFrequency(frequency: Frequency): void {
    interpretationVersion.current += 1;
    setInterpretation({ status: 'idle' });
    if (frequency !== 'custom') {
      emitPreset({ ...state, frequency });
      return;
    }
    setState({ ...state, frequency: 'custom' });
    const stored = storedCustomSchedule(state, tz);
    if (stored === null) return;
    if (stored.cron !== undefined) emitCron(stored.cron, tz);
    else onChange(stored);
    onScheduleTextChange?.(state.scheduleText || null);
  }

  /** Interpret the words as typed, unless they are blank or already being interpreted. */
  function interpret(): Promise<InterpretOutcome> | null {
    if (customText.trim().length === 0 || interpretation.status === 'busy')
      return null;
    return startInterpretation(customText);
  }

  function startInterpretation(words: string): Promise<InterpretOutcome> {
    const requestVersion = ++interpretationVersion.current;
    setInterpretation({ status: 'busy' });
    const outcome = interpretWords(words.trim(), tz).then((answer) => {
      if (requestVersion === interpretationVersion.current)
        applyOutcome(answer, words);
      return answer;
    });
    inFlight.current = outcome;
    return outcome;
  }

  function applyOutcome(outcome: InterpretOutcome, words: string): void {
    if ('failed' in outcome) {
      setInterpretation({
        status: 'unsupported',
        message: INTERPRET_FAILED,
        failed: true,
      });
      return;
    }
    if ('unsupported' in outcome) {
      setInterpretation({
        status: 'unsupported',
        message: outcome.unsupported,
        failed: false,
      });
      return;
    }
    setInterpretation({ status: 'ok', result: outcome });
    adoptSchedule(acceptedSchedule(outcome), words);
  }

  /** Make a schedule the one behind Custom mode, and tell the caller about it and its words. */
  function adoptSchedule(schedule: AutomationSchedule, words: string): void {
    setState((previous) => ({
      ...previous,
      cron: schedule.cron ?? null,
      planned: schedule.type === 'planned',
      rule: schedule.rule ?? null,
      scheduleText: words,
      cronTimezone: schedule.tz,
    }));
    if (schedule.tz !== tz) adoptedTimezone.current = schedule.tz;
    onChange(schedule);
    onScheduleTextChange?.(words);
  }

  async function settleForSave(): Promise<ScheduleForSave> {
    if (isOnDemand || !allowCustom || effectiveFrequency !== 'custom')
      return { status: 'current' };
    if (interpretation.status === 'ok')
      return readyToSave(interpretation.result, customText);
    if (interpretation.status === 'busy' && inFlight.current)
      return settleAfter(inFlight.current, customText);
    if (!customPending) return { status: 'current' };
    if (customText.trim().length === 0) return refuse(WORDS_REQUIRED);
    if (interpretation.status === 'unsupported' && !interpretation.failed)
      return refuse(interpretation.message);
    return settleAfter(startInterpretation(customText), customText);
  }

  /** Wait for the answer, unless the user changes the schedule meanwhile: then nothing is saved. */
  async function settleAfter(
    outcome: Promise<InterpretOutcome>,
    words: string,
  ): Promise<ScheduleForSave> {
    const waitingVersion = interpretationVersion.current;
    const answer = await withSaveTimeout(outcome);
    if (waitingVersion !== interpretationVersion.current)
      return { status: 'superseded' };
    return settleOutcome(answer, words);
  }

  function settleOutcome(
    outcome: InterpretOutcome,
    words: string,
  ): ScheduleForSave {
    if ('failed' in outcome) return saveWordsAsPlanned(words);
    if ('unsupported' in outcome) return refuse(outcome.unsupported);
    return readyToSave(outcome, words);
  }

  function readyToSave(
    result: AcceptedSchedule,
    words: string,
  ): ScheduleForSave {
    return {
      status: 'ready',
      schedule: acceptedSchedule(result),
      scheduleText: words,
      plan: firstPlan(result),
    };
  }

  /** No answer in time: keep the words as a planned schedule, and the planner finds the first run. */
  function saveWordsAsPlanned(words: string): ScheduleForSave {
    interpretationVersion.current += 1;
    setInterpretation({ status: 'idle' });
    const schedule: AutomationSchedule = { type: 'planned', tz };
    adoptSchedule(schedule, words);
    return { status: 'ready', schedule, scheduleText: words, plan: null };
  }

  function refuse(message: string): ScheduleForSave {
    setInterpretation({ status: 'unsupported', message, failed: false });
    customInputRef.current?.focus();
    return { status: 'refused' };
  }

  /**
   * Render a read-only indicator instead of defaulting to "Daily 09:00" — a manual automation has
   * no schedule to show, and rendering the interactive controls here would let any stray
   * interaction silently convert it to a cron schedule on save.
   */
  if (isOnDemand) {
    return (
      <p className="text-sm text-muted-foreground">
        On demand — runs only when triggered manually, not on a schedule.
      </p>
    );
  }

  if (!allowCustom && effectiveFrequency === 'custom') {
    return (
      <p className="text-sm text-muted-foreground">
        {cronHumanize(
          storedCustomSchedule(state, tz) ?? { tz },
          state.scheduleText,
        )}
      </p>
    );
  }

  const timeId = `${idPrefix}-time`;
  const timeHintId = `${idPrefix}-time-hint`;
  const freqId = `${idPrefix}-frequency`;
  const dayId = `${idPrefix}-weekday`;
  const customId = `${idPrefix}-custom`;
  const customResultId = `${idPrefix}-custom-result`;
  const timeValue = `${String(state.hour).padStart(2, '0')}:${String(state.minute).padStart(2, '0')}`;
  const busy = interpretation.status === 'busy';

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={freqId}>Frequency</Label>
          <Select
            value={effectiveFrequency}
            onValueChange={(next) => selectFrequency(next as Frequency)}
            disabled={lockFrequency}
          >
            <SelectTrigger id={freqId} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              {state.frequency === 'hourly' && (
                <SelectItem value="hourly">Hourly</SelectItem>
              )}
              {allowCustom && !lockFrequency && (
                <SelectItem value="custom">Custom</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>

        {effectiveFrequency !== 'custom' && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={timeId}>
              {effectiveFrequency === 'hourly' ? 'Minute' : 'Time'}
            </Label>
            {effectiveFrequency === 'hourly' ? (
              <input
                id={timeId}
                type="number"
                min={0}
                max={59}
                value={state.minute}
                onChange={(event) =>
                  emitPreset({
                    ...state,
                    minute: clampInt(Number(event.target.value), 0, 59, 0),
                  })
                }
                className="h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-elev-1 outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40"
              />
            ) : (
              <input
                id={timeId}
                type="time"
                aria-describedby={timeHintId}
                value={timeValue}
                onChange={(event) => {
                  const [hourText, minuteText] = event.target.value.split(':');
                  emitPreset({
                    ...state,
                    hour: clampInt(Number(hourText), 0, 23, state.hour),
                    minute: clampInt(Number(minuteText), 0, 59, state.minute),
                  });
                }}
                className="h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-elev-1 outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40"
              />
            )}
            {effectiveFrequency !== 'hourly' && (
              <p id={timeHintId} className="text-caption text-muted-foreground">
                Fires at this time in {tz}.
              </p>
            )}
          </div>
        )}
      </div>

      {effectiveFrequency === 'custom' && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={customId}>In your words</Label>
          <div className="flex items-start gap-2">
            <Input
              id={customId}
              ref={customInputRef}
              value={customText}
              maxLength={200}
              placeholder="For example: every weekday at 8:30, or the 1st and 15th at noon"
              aria-invalid={interpretation.status === 'unsupported'}
              aria-describedby={customResultId}
              onChange={(event) => {
                interpretationVersion.current += 1;
                setCustomText(event.target.value);
                setInterpretation({ status: 'idle' });
              }}
              onBlur={() => {
                if (awaitingInterpretation) void interpret();
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                  event.preventDefault();
                  void interpret();
                }
              }}
            />
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => void interpret()}
              disabled={busy || customText.trim().length === 0}
              aria-busy={busy}
            >
              {busy ? 'Interpreting…' : 'Interpret'}
            </Button>
          </div>
          <CustomResult
            id={customResultId}
            interpretation={interpretation}
            stored={customPending ? null : storedCustomSchedule(state, tz)}
            scheduleText={state.scheduleText}
            tz={tz}
          />
        </div>
      )}

      {!lockFrequency && state.frequency === 'weekly' && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={dayId}>Day of week</Label>
          <Select
            value={String(state.weekday)}
            onValueChange={(next) =>
              emitPreset({ ...state, weekday: Number(next) })
            }
          >
            <SelectTrigger id={dayId} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WEEKDAY_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={String(option.value)}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

/**
 * The line under the words: the interpreter's refusal, its summary with the next runs, or — for a
 * custom schedule that already stands and has untouched words — that schedule in plain words.
 */
function CustomResult({
  id,
  interpretation,
  stored,
  scheduleText,
  tz,
}: {
  id: string;
  interpretation: Interpretation;
  stored: AutomationSchedule | null;
  scheduleText: string;
  tz: string;
}): JSX.Element | null {
  if (interpretation.status === 'unsupported') {
    return (
      <p id={id} role="alert" className="text-caption text-destructive">
        {interpretation.message}
      </p>
    );
  }
  if (interpretation.status === 'ok') {
    const { result } = interpretation;
    const runs = result.nextFires
      .slice(0, 3)
      .map((iso) => formatRun(iso, result.tz))
      .join(', ');
    return (
      <>
        <p id={id} className="text-caption text-muted-foreground">
          Runs {asClause(result.summary)} ({result.tz}).
          {runs ? ` Next: ${runs}` : ''}
          {result.kind === 'planned' && result.runDecides && runs
            ? '. After that, each run decides the next.'
            : ''}
        </p>
        {result.kind === 'planned' && !result.rule && (
          <p className="text-caption text-muted-foreground">
            {MODEL_DATES_NOTE}
          </p>
        )}
      </>
    );
  }
  if (interpretation.status === 'idle' && stored !== null) {
    const summary = cronHumanize(stored, scheduleText);
    const timezoneLabel =
      summary === `${stored.cron} (${tz})` ? '' : ` (${tz})`;
    return (
      <p id={id} className="text-caption text-muted-foreground">
        Runs {asClause(summary)}
        {timezoneLabel}.
      </p>
    );
  }
  return null;
}
